import { useEffect, useState } from "react";
import { siteConfig } from "../config/siteConfig";

/**
 * Modern Splash. Sequence:
 *   1. Sinhala greeting "ආයුබෝවන්" fades in with elegant scale
 *   2. Romanised "Ayubowan" appears below
 *   3. Logo softly emerges
 *   4. Hotel name appears
 *   5. Blur+fade exit reveals page
 */
export default function SplashScreen({ onComplete }) {
  const [stage, setStage] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 250),
      setTimeout(() => setStage(2), 900),
      setTimeout(() => setStage(3), 1500),
      setTimeout(() => setStage(4), 2200),
      setTimeout(() => setExiting(true), 3000),
      setTimeout(() => onComplete?.(), 3700),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div
      data-testid="splash-screen"
      className={`splash-root ${exiting ? "splash-exit" : ""}`}
    >
      {/* Soft gold particles */}
      <div className="splash-particles">
        {Array.from({ length: 18 }).map((_, i) => (
          <span key={i} style={{ "--i": i }} />
        ))}
      </div>

      <div className="relative z-10 text-center px-6">
        {/* Sinhala greeting */}
        <div
          className={`transition-all duration-1000 ease-out ${
            stage >= 1 ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-90"
          }`}
        >
          <h1
            className="splash-sinhala"
            data-testid="splash-greeting"
          >
            {siteConfig.hotel.greeting}
          </h1>
        </div>

        {/* Romanised */}
        <div
          className={`mt-2 transition-all duration-700 ${
            stage >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
        >
          <p className="splash-translit">{siteConfig.hotel.greetingTrans}</p>
        </div>

        {/* Divider */}
        <div
          className={`mx-auto my-7 h-px bg-gradient-to-r from-transparent via-[#C5A059] to-transparent transition-all duration-700 ${
            stage >= 3 ? "w-48 opacity-100" : "w-0 opacity-0"
          }`}
        ></div>

        {/* Logo */}
        <img
          src={siteConfig.hotel.logoUrl}
          alt="Thuruliya"
          data-testid="splash-logo"
          className={`mx-auto w-16 h-16 sm:w-20 sm:h-20 object-contain mb-5 transition-all duration-1000 ${
            stage >= 3 ? "opacity-100 scale-100" : "opacity-0 scale-75"
          }`}
          style={{ filter: "drop-shadow(0 0 40px rgba(197,160,89,0.45))" }}
        />

        {/* Hotel name */}
        <div
          className={`transition-all duration-900 ${
            stage >= 4 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
        >
          <p
            className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#C5A059]/80 mb-2"
            data-testid="splash-title"
          >
            {siteConfig.hotel.name}
          </p>
          <p className="text-[10px] sm:text-xs tracking-[0.35em] uppercase text-[#FAF9F6]/50">
            {siteConfig.hotel.location}
          </p>
        </div>
      </div>
    </div>
  );
}
